/**
 * intake-step.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Rx";

import { AlertService } from "../../common/alert.service";
import { MessageReceiver } from "../../server/message-receiver";
import {
  AdditionalStepsRequest,
  NextStepsResponse,
  RestartAlgorithmBroadcast,
  SubmitAnswersUpstreamMessage
} from "../../server/messages/algorithms/";
import { AcknowledgmentResponse, Message, MessageTag }
  from "../../server/messages/general";
import { MessagingService } from "../../server/messaging.service";

import { AlgorithmStep, IntakeAnswer, IntakeStep } from "../structure";
import { AlgorithmService } from "./algorithm.service";
import { StepUpdate } from "./pipeline/step-update";
import { StepFacade } from "./step.facade";

/**
 * An {@link IntakeStepService} interacts with the server to negotiate
 * {@link IntakeStep}s. It receives notifications about new {@link IntakeStep}s
 * and answers, and transmits completed {@link IntakeStep}s.
 */
@Injectable()
export class IntakeStepService extends StepFacade<IntakeStep>
  implements MessageReceiver
{
  /**
   * Construct a new {@link IntakeStepService}.
   */
  public constructor (
    protected messaging : MessagingService,
    protected alerts : AlertService,
    protected algorithms : AlgorithmService
  )
  {
    super(messaging, algorithms);
  }

  /**
   * Instruct {@link StepFacade} what instances are handled by this service.
   * @param step instance to check
   */
  protected matchesSubtype (step : AlgorithmStep) : step is IntakeStep
  {
    return (step instanceof IntakeStep);
  }

  /**
   * Called when the {@link MessagingService} is ready. Listen for new steps and
   * answers coming down the pipe, and notify the user about answers issued by
   * collaborating users.
   */
  public connected () : void
  {
    // Receive next step messages and flatten them into an observable of steps.
    this.subscriptions = this.messaging.receive(MessageTag.RSP_NEXT_STEPS)
      .flatMap((message : NextStepsResponse) => message.intakeSteps)
      .map((step : IntakeStep) => new IntakeStep(step))
      .subscribe((step : IntakeStep) => this.add(step));
    // The re-construction step is because the data had only been *asserted* to
    // be IntakeStep thus far, but is actually the raw JSON. We must instantiate
    // it deliberately to get methods downstream.

    // Receive answer messages and flatten them into an observable of answers.
    // Note that despite the implied parallels with the above, the current
    // design seems to make it impossible to get multiple answers per message.
    this.subscriptions.add(
      this.messaging.receive(MessageTag.UP_SUBMIT_ANSWERS)
        .flatMap((message : SubmitAnswersUpstreamMessage) => message.answers)
        .subscribe((answer : IntakeAnswer) => this.answer(answer)));
  }

  /**
   * Teardown instructions for when the {@link MessagingService} has stopped.
   */
  public disconnected () : void {/* decorator-generated */}

  /**
   * Retrieve all registered {@link IntakeStep}s.
   */
  public all () : IntakeStep[]
  {
    this.assertRunning();
    return this.algorithms.running.steps.intakes();
  }

  /**
   * Add an {@link IntakeStep} to the context's set.
   * @param {IntakeStep} step
   */
  private add (step : IntakeStep) : void
  {
    this.assertRunning();
    this.algorithms.running.mayHaveUnexploredSteps = true;
    this.algorithms.running.steps.define(step);
  }

  /**
   * Update an {@link IntakeStep}'s answer.
   * @param  {IntakeAnswer} answer
   */
  private answer (answer : IntakeAnswer) : void
  {
    this.assertRunning();
    console.log("received", answer);
    this.algorithms.running.steps.answer(answer);
  }

  /**
   * Get an observable of {@link StepUpdate}s relevant to {@link IntakeStep}s.
   */
  public updates () : Observable<StepUpdate<any, IntakeStep>>
  {
    this.assertRunning();
    return this.algorithms.running.steps.updates.filter(
      (update : StepUpdate<AlgorithmStep, AlgorithmStep>) => (
        (update.matchesType(IntakeStep))
        // || (this.algorithms.running.steps.get(update.id)))
      )
    ) as any;
    // TODO: I can't type-wrangle this today... (it's complaining about missing
    // properties on the generics preventing the coercion)
  }

  /**
   * Submit the specified {@link IntakeAnswer}s to the server.
   *
   * @param answers
   *        An {@link Array} of {@link IntakeAnswer}s, or an atomic
   *        {@link IntakeAnswer}.
   */
  public submit (answers : IntakeAnswer[] | IntakeAnswer) : Observable<Message>
  {
    this.assertRunning();
    // If called with a singleton, wrap it in an array.
    if (!Array.isArray(answers))
    {
      answers = [answers];
    }
    return this.messaging.send(new SubmitAnswersUpstreamMessage(answers));
  }

  /**
   * Ask the server for additional {@link IntakeStep}s beyond the current set.
   * @return {Observable<Message>}
   */
  public requestMore () : Observable<Message>
  {
    this.assertRunning();
    const request : Observable<Message>
      = this.messaging.send(new AdditionalStepsRequest());
    request.subscribe((m : AcknowledgmentResponse) => {
      if (!m.ok)
      {
        this.algorithms.running.mayHaveUnexploredSteps = false;
      }
    });
    return request;
  }

  /**
   * Mark all of the current context's steps as inactive. (Used after a refresh
   * to clear the steps without losing their data.)
   * TODO: How do we handle controls with changed but unsaved data being marked
   * inactive? Should a save operation still save them? It will currently happen
   * this way because they are still within the form's controls object.
   */
  private deactivateAll () : void
  {
    this.assertRunning();
    this.algorithms.running.steps.intakes()
      .forEach((step : IntakeStep) => step.isActive = false);
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/algorithms/management/intake-step.service.ts